import { gql, useMutation } from '@apollo/client'
import { yupResolver } from '@hookform/resolvers/yup'
import AddIcon from '@mui/icons-material/Add'
import CancelIcon from '@mui/icons-material/Cancel'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Fab,
  FormHelperText,
  Grid2 as Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import Modal from 'components/Modal'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs from 'dayjs'
import moment from 'moment'
import { addMonths, addDays } from 'date-fns'
import React, { useContext, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import getDurationObj from '../../utils/getDurationObj'
import * as Yup from 'yup'

import Delete from '../Delete'
import { TASK_FRAGMENT } from '../constants'
import { TIMELINE_FRAGMENT } from '../../constants'
import TimelineContext, { TimelineContextType } from '../context'
import ColorPicker from './ColorPicker'
import { COLORS } from './ColorPicker/constants'

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
}

const taskSchema = Yup.object({
  name: Yup.string().required('Please enter a name'),
  start: Yup.date()
    .typeError('Invalid date format')
    .required('Please enter a Start Date.'),
  end: Yup.date()
    .typeError('Invalid date format')
    .required('Please enter an End Date'),
  notes: Yup.string(),
  type: Yup.string(),
  mode: Yup.string(),
  project: Yup.string(),
  assignee: Yup.string(),
  startDependencies: Yup.array()
    .of(Yup.string().required('Dependency must be a string'))
    .nullable()
    .default([]),
  endDependencies: Yup.array()
    .of(Yup.string().required('Dependency must be a string'))
    .nullable()
    .default([]),
  styles: Yup.object({
    backgroundColor: Yup.string(),
  }),
  duration: Yup.object({
    months: Yup.number()
      .typeError('Duration (months) must be a number')
      .integer('Duration (months) must be an integer')
      .min(0, 'Duration (months) cannot be negative')
      .required('Please enter duration in months'),
    days: Yup.number()
      .typeError('Duration (days) must be a number')
      .integer('Duration (days) must be an integer')
      .min(0, 'Duration (days) cannot be negative')
      .required('Please enter duration in days'),
  }),
})

export type TaskDataType = Yup.InferType<typeof taskSchema>

export type TaskDataTypeWithId = TaskDataType & {
  id?: string
}

type TaskFormProps = {
  values?: TaskDataTypeWithId
  onTask: (data: TaskDataType) => void
  onDeleteSuccess?: () => void
}

export function TaskForm(props: TaskFormProps) {
  const { onTask, values, onDeleteSuccess } = props
  const { timeline } = useContext<TimelineContextType>(TimelineContext)

  let dependencyOptions = timeline.tasks || []

  if (values?.id) {
    dependencyOptions = dependencyOptions.filter(
      option => option?.id !== values?.id
    )
  }

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { dirtyFields },
  } = useForm({
    mode: 'onChange',
    defaultValues: values || {
      name: '',
      notes: '',
      assignee: '',
      type: 'task',
      mode: 'DYNAMIC',
      startDependencies: [],
      endDependencies: [],
      start: new Date(),
      styles: {
        backgroundColor: COLORS[0],
      },
      duration: {
        months: 1,
        days: 0,
      },
    },
    resolver: yupResolver(taskSchema),
  })

  const start = watch('start')
  const durationMonths = watch('duration.months')
  const durationDays = watch('duration.days')
  const startDependencies = watch('startDependencies')
  const endDependencies = watch('endDependencies')

  useEffect(() => {
    if (
      start &&
      durationMonths != null &&
      durationDays != null &&
      durationMonths >= 0 &&
      durationDays >= 0
    ) {
      const newEnd = moment(start)
        .add(durationMonths, 'months')
        .add(durationDays, 'days')
        .toDate()

      setValue('end', newEnd)
    }
  }, [start, durationMonths, durationDays, setValue])

  useEffect(() => {
    if (
      dirtyFields.startDependencies &&
      startDependencies &&
      startDependencies.length > 0
    ) {
      const taskDependencies = (timeline.tasks || []).filter(task =>
        startDependencies.includes(task?.id || '')
      )

      const maxStart = Math.max(
        ...taskDependencies.map(task => {
          const millis = task?.end
            ? new Date(task.end).getTime()
            : addMonths(
                addDays(
                  new Date(),
                  (task?.duration?.days || 0) + (task?.offset?.days || 0)
                ),
                (task?.duration?.months || 0) + (task?.offset?.months || 0)
              ).getTime()

          return millis
        })
      )

      setValue('start', new Date(maxStart))
    }
  }, [
    startDependencies,
    setValue,
    timeline.tasks,
    dirtyFields.startDependencies,
  ])

  useEffect(() => {
    if (
      dirtyFields.endDependencies &&
      endDependencies &&
      endDependencies.length > 0
    ) {
      const taskDependencies = (timeline.tasks || []).filter(task =>
        endDependencies.includes(task?.id || '')
      )

      const durationInMonths = Math.max(
        ...taskDependencies.map(task => task?.duration?.months || 0)
      )
      const durationInDays = Math.max(
        ...taskDependencies.map(task => task?.duration?.days || 0)
      )

      setValue('duration.months', durationInMonths)
      setValue('duration.days', durationInDays)
    }
  }, [endDependencies, setValue, timeline.tasks, dirtyFields.endDependencies])

  const handleMultiSelectRenderValues = (
    selected: string[]
  ): React.ReactNode => {
    const selectedNames = selected
      .map(id => (timeline.tasks || []).find(task => task?.id === id)?.name)
      .filter(Boolean)

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selectedNames.map(value => (
          <Chip size="small" key={value} label={value} />
        ))}
      </Box>
    )
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onTask)}>
      <Grid container spacing={2}>
        <Grid size={6}>
          <Controller
            name="name"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <React.Fragment>
                <InputLabel htmlFor="name">
                  <Typography sx={theme => ({ ...theme.typography.caption })}>
                    Name*
                  </Typography>
                </InputLabel>
                <TextField
                  id="name"
                  error={!!error}
                  onChange={onChange}
                  value={value}
                  fullWidth
                  variant="outlined"
                  size="small"
                  helperText={error && error.message}
                />
              </React.Fragment>
            )}
          />
        </Grid>
        <Grid size={3}>
          <Controller
            name="mode"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <React.Fragment>
                <InputLabel>
                  <Typography sx={theme => ({ ...theme.typography.caption })}>
                    Mode
                  </Typography>
                </InputLabel>
                <Select
                  fullWidth
                  size="small"
                  error={!!error}
                  value={value}
                  onChange={(event: SelectChangeEvent): void =>
                    onChange(event.target.value as string)
                  }
                >
                  <MenuItem value="DYNAMIC">Dynamic</MenuItem>
                  <MenuItem value="STATIC">Static</MenuItem>
                </Select>
                {error && (
                  <FormHelperText error>{error.message}</FormHelperText>
                )}
              </React.Fragment>
            )}
          />
        </Grid>
        <Grid size={3}>
          <Controller
            name="type"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <React.Fragment>
                <InputLabel>
                  <Typography sx={theme => ({ ...theme.typography.caption })}>
                    Type
                  </Typography>
                </InputLabel>
                <Select
                  fullWidth
                  size="small"
                  error={!!error}
                  value={value}
                  onChange={(event: SelectChangeEvent): void =>
                    onChange(event.target.value as string)
                  }
                >
                  <MenuItem value="task">Task</MenuItem>
                </Select>
                {error && (
                  <FormHelperText error>{error.message}</FormHelperText>
                )}
              </React.Fragment>
            )}
          />
        </Grid>
        <Grid size={4}>
          <Controller
            name="assignee"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <React.Fragment>
                <InputLabel htmlFor="assignee">
                  <Typography sx={theme => ({ ...theme.typography.caption })}>
                    Assignee
                  </Typography>
                </InputLabel>
                <TextField
                  id="assignee"
                  error={!!error}
                  onChange={onChange}
                  value={value}
                  fullWidth
                  variant="outlined"
                  size="small"
                  helperText={error && error.message}
                />
              </React.Fragment>
            )}
          />
        </Grid>
        <Grid size={4}>
          <Controller
            name="startDependencies"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <React.Fragment>
                <InputLabel>
                  <Typography sx={theme => ({ ...theme.typography.caption })}>
                    Start Dependencies
                  </Typography>
                </InputLabel>
                <Select
                  {...field}
                  value={field.value ?? []}
                  fullWidth
                  size="small"
                  error={!!error}
                  multiple
                  renderValue={handleMultiSelectRenderValues}
                  MenuProps={MenuProps}
                >
                  {dependencyOptions.map(task => (
                    <MenuItem key={task?.id || ''} value={task?.id || ''}>
                      <Checkbox
                        checked={field.value?.includes(task?.id || '')}
                      />
                      <ListItemText primary={task?.name} />
                    </MenuItem>
                  ))}
                </Select>
                {error && (
                  <FormHelperText error>{error.message}</FormHelperText>
                )}
              </React.Fragment>
            )}
          />
        </Grid>
        <Grid size={4}>
          <Controller
            name="endDependencies"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <React.Fragment>
                <InputLabel>
                  <Typography sx={theme => ({ ...theme.typography.caption })}>
                    End Dependencies
                  </Typography>
                </InputLabel>
                <Select
                  {...field}
                  value={field.value ?? []}
                  fullWidth
                  size="small"
                  error={!!error}
                  multiple
                  renderValue={handleMultiSelectRenderValues}
                  MenuProps={MenuProps}
                >
                  {dependencyOptions.map(task => (
                    <MenuItem key={task?.id || ''} value={task?.id || ''}>
                      <Checkbox
                        checked={field.value?.includes(task?.id || '')}
                      />
                      <ListItemText primary={task?.name} />
                    </MenuItem>
                  ))}
                </Select>
                {error && (
                  <FormHelperText error>{error.message}</FormHelperText>
                )}
              </React.Fragment>
            )}
          />
        </Grid>
        <Grid size={4}>
          <Controller
            name="start"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <React.Fragment>
                <InputLabel htmlFor="start">
                  <Typography sx={theme => ({ ...theme.typography.caption })}>
                    Start Date*
                  </Typography>
                </InputLabel>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={value ? dayjs(new Date(value)) : dayjs()}
                    onChange={date => onChange(date?.toDate())}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        error: !!error,
                      },
                    }}
                  />
                </LocalizationProvider>
                {error && (
                  <FormHelperText error>{error.message}</FormHelperText>
                )}
              </React.Fragment>
            )}
          />
        </Grid>
        <Grid size={2}>
          <Controller
            name="duration.months"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <React.Fragment>
                <InputLabel htmlFor="duration.months">
                  <Typography sx={theme => ({ ...theme.typography.caption })}>
                    Duration Months
                  </Typography>
                </InputLabel>
                <TextField
                  id="duration.months"
                  disabled={!start}
                  inputProps={{ min: 0, step: 1 }}
                  error={!!error}
                  onChange={onChange}
                  value={value}
                  fullWidth
                  type="number"
                  variant="outlined"
                  size="small"
                />
              </React.Fragment>
            )}
          />
        </Grid>
        <Grid size={2}>
          <Controller
            name="duration.days"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <React.Fragment>
                <InputLabel htmlFor="duration.days">
                  <Typography sx={theme => ({ ...theme.typography.caption })}>
                    Duration Days
                  </Typography>
                </InputLabel>
                <TextField
                  id="duration.days"
                  error={!!error}
                  disabled={!start}
                  onChange={onChange}
                  inputProps={{ min: 0, step: 1 }}
                  value={value}
                  fullWidth
                  type="number"
                  variant="outlined"
                  size="small"
                />
              </React.Fragment>
            )}
          />
        </Grid>
        <Grid size={4}>
          <Controller
            name="end"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <React.Fragment>
                <InputLabel htmlFor="end">
                  <Typography sx={theme => ({ ...theme.typography.caption })}>
                    End Date*
                  </Typography>
                </InputLabel>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={value ? dayjs(new Date(value)) : dayjs()}
                    onChange={date => onChange(date?.toDate())}
                    disabled
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        error: !!error,
                      },
                    }}
                  />
                </LocalizationProvider>
                {error && (
                  <FormHelperText error>{error.message}</FormHelperText>
                )}
              </React.Fragment>
            )}
          />
        </Grid>
        <Grid size={12}>
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <React.Fragment>
                <InputLabel htmlFor="notes">
                  <Typography sx={theme => ({ ...theme.typography.caption })}>
                    Notes
                  </Typography>
                </InputLabel>
                <TextField
                  {...field}
                  placeholder="Your notes..."
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  variant="outlined"
                />
              </React.Fragment>
            )}
          />
        </Grid>
        <Grid size={12}>
          <Stack spacing={1}>
            <Typography>Color</Typography>
            <Controller
              name="styles.backgroundColor"
              control={control}
              render={({ field }) => <ColorPicker {...field} />}
            />
          </Stack>
        </Grid>
      </Grid>
      <Divider sx={{ mt: 1, mb: 1 }} />
      <Stack
        direction="row"
        sx={{
          justifyContent: values ? 'space-between' : 'end',
          alignItems: 'center',
        }}
      >
        {values && <Delete task={values} onDeleteSuccess={onDeleteSuccess} />}
        <Button
          type="submit"
          sx={{ float: 'right' }}
          variant="contained"
          disableElevation
        >
          Save
        </Button>
      </Stack>
    </Box>
  )
}

const CREATE_TASK = gql`
  ${TIMELINE_FRAGMENT}
  ${TASK_FRAGMENT}
  mutation CREATE_TASK($timelineId: ID!, $task: TaskInput!) {
    createTask(timelineId: $timelineId, task: $task) {
      ...TimelineFragment
      tasks {
        ...TaskFragment
      }
    }
  }
`

function Create() {
  const { timeline } = useContext<TimelineContextType>(TimelineContext)

  const [open, setOpen] = useState<boolean>(false)
  const theme = useTheme()

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const [createTask] = useMutation(CREATE_TASK)

  const handleTimeline = (data: TaskDataType) => {
    const { start, end, mode, ...restTask } = data
    createTask({
      variables: {
        timelineId: timeline.id,
        task: {
          type: 'task',
          progress: 0,
          ...restTask,
          mode,
          ...(mode === 'STATIC' ? { start, end } : { start: null, end: null }),
          offset: getDurationObj(new Date(), start),
        },
      },
      onCompleted: handleClose,
    })
  }

  return (
    <React.Fragment>
      <Modal open={open} width="75%" maxWidth={720}>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography
            sx={{ ...theme.typography.h5, color: theme.palette.text.primary }}
          >
            Create Task
          </Typography>
          <CancelIcon
            onClick={handleClose}
            sx={{ cursor: 'pointer', color: theme.palette.error.main }}
          />
        </Stack>
        <Divider sx={{ mt: 1, mb: 1 }} />
        <TaskForm onTask={handleTimeline} />
      </Modal>
      <Fab
        color="primary"
        aria-label="add"
        onClick={handleOpen}
        sx={{ position: 'fixed', bottom: 24, right: 24 }}
      >
        <AddIcon />
      </Fab>
    </React.Fragment>
  )
}

export default Create
